import { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  CardHeader,
  CardContent,
  CardActions,
  DataGrid,
  DatePicker,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  Loading,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Chip,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Icon,
} from "../../../../components";
import {
  OrcamentoServicosService,
  OrcamentoServicosItensService,
  OrcamentoServicosServicosService,
  ProdutosService,
  history,
  PessoaService,
  OrcamentoServicosPagamentosService,
  MeioPagamentoService,
} from "../../../../services";
import { formatDate, formatPrice, responseErros } from "../../../../utils";
import { format } from "date-fns";
import { calcularValorTotal } from "../../add-ordem-servico/add-ordem-servico";

const filtrosIniciasPaginacao = {
  page: 1,
  limit: 10,
  orderBy: "updatedAt",
  order: "desc",
};

const ImportarOrcamentoServicoDialog = ({ open, setOpen, prestadoresList }) => {
  const orcamentoServicoService = new OrcamentoServicosService();
  const orcamentoServicosItensService = new OrcamentoServicosItensService();
  const orcamentoServicosServicosService =
    new OrcamentoServicosServicosService();
  const orcamentoServicosPagamentosService =
    new OrcamentoServicosPagamentosService();
  const produtosService = new ProdutosService();
  const pessoaService = new PessoaService();
  const meioPagamentoService = new MeioPagamentoService();

  const [loading, setLoading] = useState(false);
  const [servicos, setServicos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [pagamentos, setPagamentos] = useState([]);
  const [listaPessoas, setListaPessoas] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [orcamentosServicos, setOrcamentosServicos] = useState([]);

  const [orcamentoServicoSelecionada, setOrcamentoServicoSelecionada] =
    useState(null);
  const [filtros, setFiltros] = useState({
    ...filtrosIniciasPaginacao,
    status: "ABERTO",
  });

  const [query, setQuery] = useState("");
  const [openFiltroDialog, setOpenFiltroDialog] = useState(false);
  const [filtrosAvancados, setFiltrosAvancados] = useState({});
  const [clienteList, setClienteList] = useState([]);

  const colunasServicos = [
    {
      field: "codigo",
      headerName: "Código",
      flex: 100,
    },
    {
      field: "descricao",
      headerName: "Serviço",
      flex: 500,
    },
    {
      field: "prestadorServicoId",
      headerName: "P. de Servico",
      flex: 150,
      sortable: false,
      filtroPessoa: true,
      valueGetter: (params) =>
        prestadoresList?.find((item) => item.id == params.value)
          ?.nomeRazaoSocial,
    },
    {
      field: "quantidade",
      headerName: "Qtd.",
      flex: 90,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "valor",
      headerName: "Valor (R$)",
      flex: 200,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        formatPrice(parseFloat(params.value || 0), false),
    },
    {
      field: "subtotal",
      headerName: "Subtotal (R$)",
      flex: 180,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        formatPrice(
          parseFloat(params.row.quantidade) * parseFloat(params.row.valor),
          false,
        ),
    },
    {
      field: "desconto",
      headerName: "Desconto (R$)",
      flex: 200,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        formatPrice(parseFloat(params.value || 0), false),
    },
    {
      field: "totalItem",
      headerName: "Total",
      flex: 180,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) => {
        return formatPrice(
          parseFloat(params.row.valor) * parseFloat(params.row.quantidade) -
            parseFloat(params.row.desconto || 0),
          false,
        );
      },
    },
  ];

  const colunasProdutos = [
    {
      field: "codigo",
      headerName: "Cod.",
      flex: 100,
    },
    {
      field: "descricao",
      headerName: "Produto",
      flex: 400,
    },
    {
      field: "quantidade",
      headerName: "Qtd.",
      headerAlign: "center",
      align: "center",
      width: 100,
    },
    {
      field: "valorUnitario",
      headerName: "Unitário (R$)",
      headerAlign: "right",
      align: "right",
      flex: 200,
      valueGetter: (params) => formatPrice(parseFloat(params.value), false),
    },
    {
      field: "desconto",
      headerName: "Desconto (R$)",
      flex: 200,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        formatPrice(parseFloat(params.value || 0), false),
    },
    {
      field: "totalItem",
      headerName: "Total (R$)",
      flex: 200,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        params.value ? formatPrice(parseFloat(params.value), false) : "",
    },
  ];

  const colunasPagamentos = [
    {
      field: "descricao",
      headerName: "Meio de Pagamento",
      flex: 200,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "valorTitulo",
      headerName: "Total (R$)",
      flex: 50,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        params.value ? formatPrice(parseFloat(params.value), false) : "",
    },
  ];

  useEffect(() => {
    if (open && orcamentosServicos.length === 0) {
      buscarOrcamentoServicos({ ...filtros });
    }
  }, [open]);

  const buscarOrcamentoServicos = async (filtrosBusca) => {
    setLoading(true);
    const result = await orcamentoServicoService.getAll(filtrosBusca);
    if (!result.isAxiosError) {
      setOrcamentosServicos(result.data.rows);
      setTotalItems(result.data.count);
      buscarRelacionamentosOrcamentoServicos(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarRelacionamentosOrcamentoServicos = (listaOrcamento) => {
    const idsPessoas = [];
    for (const objeto of listaOrcamento) {
      if (objeto.clienteId) idsPessoas.push(objeto.clienteId);
    }
    buscarPessoas([...new Set(idsPessoas)]);
  };

  const buscarPessoas = (listaIds) => {
    if (listaIds?.length) {
      const filtro = {
        id: listaIds,
        nonPaginated: true,
        attributes: ["id", "nomeRazaoSocial"],
        disabledInclude: true,
      };
      pessoaService.getAll(filtro).then((result) => {
        if (!result.isAxiosError) {
          const listaPessoas = result.data;
          setListaPessoas(listaPessoas);
        } else {
          responseErros(result);
        }
      });
    } else setListaPessoas([]);
  };

  const buscarProdutosAvancado = (itens) => {
    const produtosIds = itens.map(({ produtoId }) => produtoId);
    const filtro = {
      id: produtosIds,
      ativado: true,
      nonPaginated: true,
    };
    produtosService.getAllProdutosSimples(filtro).then((result) => {
      if (!result.isAxiosError) {
        preencherProdutos(itens, result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const preencherProdutos = (itensBusca, produtosRetornados) => {
    const produtosPreenchidos = itensBusca.map((item) => {
      const produtoBusca = produtosRetornados.find(
        (produto) => produto.id == item.produtoId,
      );
      return {
        ...item,
        descricao: produtoBusca?.descricao,
        totalItem: calcularValorTotal(item),
      };
    });
    setLoading(false);
    setProdutos(produtosPreenchidos);
  };

  const buscarMeiosPagamento = (pagamentos) => {
    const pagamentoIds = pagamentos.map(
      ({ meioPagamentoId }) => meioPagamentoId,
    );
    const filtro = {
      id: pagamentoIds,
      ativado: true,
      nonPaginated: true,
    };
    meioPagamentoService.getAllFiltroAvancado(filtro).then((result) => {
      if (!result.isAxiosError) {
        const pagamentosPreenchidos = pagamentos.map((item) => {
          const meioPagamentoSelecionado = result.data.find(
            (pagamento) => pagamento.id == item.meioPagamentoId,
          );
          return {
            ...item,
            descricao: meioPagamentoSelecionado?.descricao,
            meioPagamentoSelecionado,
          };
        });
        setLoading(false);
        setPagamentos(pagamentosPreenchidos);
      } else {
        setLoading(false);
        responseErros(result);
      }
    });
  };

  const handlePageChange = (newPage) => {
    const novosFiltros = { ...filtros, page: newPage };
    setFiltros(novosFiltros);
    buscarOrcamentoServicos({ ...novosFiltros });
    setOrcamentoServicoSelecionada(null);
    setServicos([]);
    setProdutos([]);
  };

  const handleBusca = (e) => {
    if (e) e.preventDefault();
    const novosFiltros = { ...filtrosIniciasPaginacao };
    novosFiltros.numeroDocumento = query;
    setFiltros(novosFiltros);
    buscarOrcamentoServicos({ ...novosFiltros });
    setOrcamentoServicoSelecionada(null);
    setServicos([]);
    setProdutos([]);
  };

  const handleOrcamentoServicoClick = async (orcamentoServico) => {
    setOrcamentoServicoSelecionada(orcamentoServico);
    setLoading(true);

    try {
      const body = {
        orcamentoServicoId: orcamentoServico.id,
        nonPaginated: true,
      };

      const resServicos = await orcamentoServicosServicosService.getAll(body);
      if (!resServicos.isAxiosError) {
        setServicos(resServicos.data);
      } else {
        responseErros(resServicos);
        setServicos([]);
      }

      const resItens = await orcamentoServicosItensService.getAll(body);
      if (!resItens.isAxiosError) {
        if (resItens.data && resItens.data.length > 0) {
          buscarProdutosAvancado(resItens.data);
        } else {
          setProdutos([]);
        }
      } else {
        responseErros(resItens);
        setProdutos([]);
      }

      const resPagamentos = await orcamentoServicosPagamentosService.getAll(
        body,
      );
      if (!resPagamentos.isAxiosError) {
        if (resPagamentos.data && resPagamentos.data.length > 0) {
          buscarMeiosPagamento(resPagamentos.data);
        } else {
          setLoading(false);
          setPagamentos([]);
        }
      } else {
        responseErros(resServicos);
        setPagamentos([]);
        setLoading(false);
      }
    } catch (error) {
      console.error("Erro ao buscar detalhes do Orçamento de Serviço:", error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setServicos([]);
    setProdutos([]);
    setOrcamentoServicoSelecionada(null);
  };

  const buscarClientesFiltro = () => {
    pessoaService
      .getAll({
        nonPaginated: true,
        tiposPessoaId: 1,
        attributes: ["id", "nomeRazaoSocial"],
      })
      .then((result) => {
        if (!result.isAxiosError) {
          setClienteList(result.data);
        } else {
          responseErros(result);
        }
      });
  };

  const onChangeFiltroInput = (event) => {
    setFiltrosAvancados((prev) => ({
      ...prev,
      [event.target.name]: event.target.value || null,
    }));
  };

  const onChangeFiltroAutocomplete = (name, value) => {
    setFiltrosAvancados((prev) => ({
      ...prev,
      [name]: value ? value.id : undefined,
    }));
  };

  const onChangeFiltroDate = (name, date) => {
    const isValidDate = (d) => d instanceof Date && !isNaN(d);
    if (!date || !isValidDate(date) || date.getFullYear() <= 2000) return;
    setFiltrosAvancados((prev) => ({
      ...prev,
      [name]: formatDate.toSend(date),
    }));
  };

  const valueFiltroInput = (name) => filtrosAvancados[name] ?? "";
  const valueFiltroDate = (name) => filtrosAvancados[name] ?? null;
  const valueFiltroAutocomplete = (lista, name) =>
    lista.find((o) => o.id === filtrosAvancados[name]) ?? undefined;

  const handleAplicarFiltros = async () => {
    setLoading(true);
    const porIntervalo = [];
    const filtrosAvancadosTemp = { ...filtrosAvancados };
    if (
      filtrosAvancadosTemp.dataAberturaStart &&
      filtrosAvancadosTemp.dataAberturaEnd
    ) {
      porIntervalo.push({
        coluna: "createdAt",
        de: filtrosAvancadosTemp.dataAberturaStart,
        ate: filtrosAvancadosTemp.dataAberturaEnd,
      });
    }
    delete filtrosAvancadosTemp.dataAberturaStart;
    delete filtrosAvancadosTemp.dataAberturaEnd;
    const result = await orcamentoServicoService.getAllFiltroAvancado({
      ...filtrosAvancadosTemp,
      status: "ABERTO",
      restritiva: true,
      porIntervalo,
    });
    if (!result.isAxiosError) {
      setOrcamentosServicos(result.data.rows);
      setTotalItems(result.data.count);
      buscarRelacionamentosOrcamentoServicos(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
    setOrcamentoServicoSelecionada(null);
    setServicos([]);
    setProdutos([]);
    setOpenFiltroDialog(false);
  };

  const importarOrcamentoServico = async (idOs) => {
    const orcamentoServico = await buscarOrcamentoServicosById(idOs);
    const orcamentoServicoEntity = {
      ...orcamentoServico,
      cliente: listaPessoas.find(
        (pessoa) => pessoa.id === orcamentoServico.clienteId,
      ),
      dataAbertura: new Date(),
      itens: produtos.length > 0 ? produtos : orcamentoServico.itens,
      pagamentos,
      numeroDocumento: null,
      orcamentoServicoId: orcamentoServico.id,
    };
    window.ordemServicoEntity = orcamentoServicoEntity;
    history.push("/faturamento/ordem-servicos/importar-orcamento-servico");
  };

  const buscarOrcamentoServicosById = async (id) => {
    setLoading(true);
    const result = await orcamentoServicoService.getById(id);
    if (!result.isAxiosError) {
      return result.data;
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const totalPaginas = Math.ceil(totalItems / filtros.limit);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          minHeight: "75vh",
          maxHeight: "90vh",
        },
      }}
    >
      <div className="px-3">
        <CardHeader
          title="Importar Orçamento de Serviço"
          action={
            <IconButton
              color="inherit"
              onClick={handleClose}
              sx={{
                backgroundColor: "#EDEEF0",
                "&:hover": { backgroundColor: "#B6BABC" },
              }}
            >
              <i
                className="ph-bold ph-x"
                style={{ fontSize: "clamp(17px, 4vw, 20px)" }}
              ></i>
            </IconButton>
          }
        />
      </div>

      <DialogContent sx={{ p: 1, overflow: "hidden" }}>
        <Grid container sx={{ minHeight: "60vh" }}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              borderRight: "1px solid",
              borderColor: "divider",
              p: 0,
            }}
          >
            <Box sx={{ px: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ mt: 1, mb: 3 }}
              >
                Lista de Orçamentos de Serviço
              </Typography>

              <Box
                component="form"
                onSubmit={handleBusca}
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  gap: 1,
                  mb: 3,
                  height: "40px",
                }}
              >
                <Box sx={{ flex: 1, height: "100%" }}>
                  <TextField
                    fullWidth
                    label="Pesquisar por Nº Documento"
                    variant="outlined"
                    size="medium"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    height: "50px",
                    minWidth: "100px",
                  }}
                >
                  Buscar
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => {
                    buscarClientesFiltro();
                    setOpenFiltroDialog(true);
                  }}
                  sx={{
                    height: "50px",
                    minWidth: "100px",
                  }}
                >
                  Filtro{" "}
                  <i
                    className="ph ph-funnel-simple"
                    style={{ fontSize: 18, color: "#2196f3", marginLeft: 8 }}
                  ></i>
                </Button>
              </Box>

              {orcamentosServicos.length > 0 ? (
                <List sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}>
                  {orcamentosServicos.map((os) => {
                    return (
                      <ListItemButton
                        key={os.id}
                        sx={{
                          borderRadius: "8px",
                          mb: 2,
                          py: 1,
                          px: 2,
                          border:
                            orcamentoServicoSelecionada?.id === os.id
                              ? "2px solid #2196f3"
                              : "1px solid transparent",
                          backgroundColor:
                            orcamentoServicoSelecionada?.id === os.id
                              ? "#e3f2fd"
                              : "white",
                          boxShadow:
                            orcamentoServicoSelecionada?.id === os.id
                              ? "0 4px 12px rgba(25, 118, 210, 0.15)"
                              : "0 2px 4px rgba(0, 0, 0, 0.05)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            backgroundColor:
                              orcamentoServicoSelecionada?.id === os.id
                                ? "#e3f2fd"
                                : "#f5f5f5",
                            transform: "translateY(-1px)",
                            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.1)",
                          },
                        }}
                        disableRipple
                        selected={orcamentoServicoSelecionada?.id === os.id}
                        onClick={() => handleOrcamentoServicoClick(os)}
                      >
                        <ListItemText
                          primary={`Orçamento: ${os?.numeroDocumento || ""}`}
                          secondary={
                            <>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ whiteSpace: "pre-line" }}
                              >
                                Cliente:{" "}
                                {listaPessoas.find(
                                  (pessoa) => pessoa.id === os.clienteId,
                                )?.nomeRazaoSocial || "Sem Cliente"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ whiteSpace: "pre-line" }}
                              >
                                Data de Criação:{" "}
                                {format(
                                  formatDate.received(os.createdAt),
                                  "dd/MM/yyyy",
                                )}
                              </Typography>
                            </>
                          }
                        />

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              ml: 1,
                            }}
                          >
                            <Tooltip title="ABERTO" arrow placement="right">
                              <Icon
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <i
                                  className="ph-fill ph-circle"
                                  style={{
                                    color: "#4FBB53",
                                    fontSize: 18,
                                  }}
                                ></i>
                              </Icon>
                            </Tooltip>
                          </Box>
                        </Box>
                      </ListItemButton>
                    );
                  })}
                </List>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "200px",
                    p: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", fontWeight: 500 }}
                  >
                    Nenhuma ordem de serviço encontrada.
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              py: 1,
              px: 2,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "white",
              height: "100%",
            }}
          >
            {orcamentoServicoSelecionada ? (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflowY: "auto",
                  maxHeight: "60vh",
                  pr: 1,
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {`Detalhes do Orçamento de Serviço - Orçamento: ${
                      orcamentoServicoSelecionada?.numeroDocumento || ""
                    }`}
                  </Typography>
                </Box>

                {servicos.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
                    >
                      Serviços
                    </Typography>
                    <DataGrid
                      rows={servicos}
                      columns={colunasServicos}
                      loading={false}
                      disableSelectionOnClick
                      noFilter
                      disableAcoes
                      hideFooter
                      autoHeight
                    />
                  </Box>
                )}

                {produtos.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
                    >
                      Produtos
                    </Typography>
                    <DataGrid
                      rows={produtos}
                      columns={colunasProdutos}
                      loading={loading}
                      disableSelectionOnClick
                      noFilter
                      disableAcoes
                      hideFooter
                      autoHeight
                    />
                  </Box>
                )}

                {pagamentos.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
                    >
                      Pagamentos
                    </Typography>
                    <DataGrid
                      rows={pagamentos}
                      columns={colunasPagamentos}
                      loading={loading}
                      disableSelectionOnClick
                      noFilter
                      disableAcoes
                      hideFooter
                      autoHeight
                    />
                  </Box>
                )}

                {servicos.length === 0 && produtos.length === 0 && !loading && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", mt: 4 }}
                  >
                    Nenhum serviço ou produto lançado neste orçamento de
                    serviço.
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                  }}
                >
                  <i
                    className="ph ph-arrow-left"
                    style={{ fontSize: 36, color: "#bdbdbd" }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 500, textAlign: "center" }}
                >
                  Selecione um Orçamento de Serviço
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", maxWidth: 280 }}
                >
                  Clique em um Orçamento de Serviço na lista ao lado para
                  visualizar seus serviços e produtos detalhados.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          pb: 3,
        }}
      >
        <Box
          className="d-flex justify-content-center align-items-center gap-2"
          sx={{ px: "2.5rem" }}
        >
          <Button
            onClick={() => handlePageChange(filtros.page - 1)}
            disabled={filtros.page === 1}
          >
            Anterior
          </Button>
          <Chip
            label={` Página ${filtros.page} de ${
              totalPaginas === 0 ? 1 : totalPaginas
            }`}
          />
          <Button
            onClick={() => handlePageChange(filtros.page + 1)}
            disabled={filtros.page === totalPaginas || totalPaginas === 0}
          >
            Próxima
          </Button>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", pr: 3 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={(e) => {
              e.stopPropagation();
              importarOrcamentoServico(orcamentoServicoSelecionada?.id);
            }}
            disabled={!orcamentoServicoSelecionada}
          >
            <i
              className="ph-fill ph-file-arrow-up"
              style={{ fontSize: 20, marginRight: 8, color: "#fff" }}
            ></i>
            Importar Orçamento de Serviço
          </Button>
        </Box>
      </DialogActions>
      <Loading loading={loading} />
      <Dialog
        maxWidth="md"
        fullWidth
        open={openFiltroDialog}
        onClose={() => setOpenFiltroDialog(false)}
      >
        <DialogContent>
          <CardHeader title="Pesquisa Avançada" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  id="numeroDocumento"
                  name="numeroDocumento"
                  label="Nº Documento"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  onChange={onChangeFiltroInput}
                  value={valueFiltroInput("numeroDocumento")}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl variant="outlined" margin="normal" fullWidth>
                  <Autocomplete
                    id="clienteId"
                    name="clienteId"
                    options={clienteList}
                    noOptionsText="Sem opções"
                    autoHighlight
                    getOptionLabel={(option) => option?.nomeRazaoSocial ?? ""}
                    getOptionSelected={(option, value) =>
                      option?.nomeRazaoSocial === value?.nomeRazaoSocial
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente"
                        variant="outlined"
                      />
                    )}
                    onChange={(e, value) =>
                      onChangeFiltroAutocomplete("clienteId", value)
                    }
                    value={valueFiltroAutocomplete(clienteList, "clienteId")}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  id="dataAberturaStart"
                  name="dataAberturaStart"
                  label="Data Abertura De"
                  format="dd/MM/yyyy"
                  value={valueFiltroDate("dataAberturaStart")}
                  onChange={(date) =>
                    onChangeFiltroDate("dataAberturaStart", date)
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  id="dataAberturaEnd"
                  name="dataAberturaEnd"
                  label="Data Abertura Até"
                  format="dd/MM/yyyy"
                  value={valueFiltroDate("dataAberturaEnd")}
                  onChange={(date) =>
                    onChangeFiltroDate("dataAberturaEnd", date)
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAplicarFiltros}
            >
              <i
                className="ph-fill ph-check-circle"
                style={{ fontSize: 22, marginRight: 10 }}
              ></i>
              Filtrar
            </Button>
            <Button
              variant="contained"
              color="inherit"
              onClick={() => {
                setFiltrosAvancados({});
                setOpenFiltroDialog(false);
              }}
            >
              <i
                className="ph-fill ph-x-circle"
                style={{ fontSize: 22, marginRight: 10 }}
              ></i>
              Fechar
            </Button>
          </CardActions>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ImportarOrcamentoServicoDialog;
